import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, LockKeyhole, Send, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

import api from '../utils/api';
import CosmicBackground from '../components/CosmicBackground';
import LanguageSelector from '../components/LanguageSelector';
import Logo from '../components/Logo';
import { useLanguage } from '../contexts/LanguageContext';

const GuestAsk = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();

  const [question, setQuestion] = useState(
    sessionStorage.getItem('pendingGuestQuestion') || ''
  );
  const [preview, setPreview] = useState(
    sessionStorage.getItem('pendingGuestPreview') || ''
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const copy = useMemo(() => {
    const translations = {
      ro: {
        eyebrow: 'ÎNTREBAREA TA COSMICĂ',
        title: 'Întreabă Universul',
        subtitle: 'Scrie întrebarea care îți ocupă gândurile. Universul îți va dezvălui începutul răspunsului.',
        label: 'Ce ai vrea să întrebi?',
        placeholder: 'Exemplu: Este sufletul meu pereche deja în viața mea?',
        submit: 'Primește răspunsul',
        loading: 'Universul îți pregătește mesajul…',
        begun: 'MESAJUL TĂU A ÎNCEPUT',
        answered: 'Universul ți-a răspuns',
        lockedTitle: 'Restul mesajului te așteaptă',
        lockedText: 'Creează-ți contul pentru a vedea răspunsul complet și pentru a-l păstra în istoricul tău.',
        unlock: 'Creează cont și dezvăluie mesajul',
        another: 'Pune altă întrebare',
        back: 'Înapoi',
        empty: 'Scrie mai întâi întrebarea.',
        short: 'Scrie puțin mai mult pentru ca Universul să înțeleagă.',
        disclaimer: 'Doar în scop recreativ, de divertisment și reflecție personală.',
      },
      en: {
        eyebrow: 'YOUR COSMIC QUESTION',
        title: 'Ask the Universe',
        subtitle: 'Write the question on your mind. The Universe will reveal the beginning of your answer.',
        label: 'What would you like to ask?',
        placeholder: 'Example: Is my soulmate already in my life?',
        submit: 'Receive your answer',
        loading: 'The Universe is preparing your message…',
        begun: 'YOUR MESSAGE HAS BEGUN',
        answered: 'The Universe has answered',
        lockedTitle: 'The rest of your message is waiting',
        lockedText: 'Create your account to reveal the complete answer and save it in your personal history.',
        unlock: 'Create account and reveal message',
        another: 'Ask another question',
        back: 'Back',
        empty: 'Please write your question first.',
        short: 'Please write a little more so the Universe can understand.',
        disclaimer: 'For entertainment and personal reflection only.',
      },
    };
    return translations[language] || translations.en;
  }, [language]);

  const clearPending = () => {
    ['pendingGuestQuestion', 'pendingGuestPreview', 'pendingGuestToken', 'pendingGuestCreatedAt']
      .forEach((key) => sessionStorage.removeItem(key));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const cleanQuestion = question.trim();

    if (!cleanQuestion) {
      setError(copy.empty);
      return;
    }
    if (cleanQuestion.length < 5) {
      setError(copy.short);
      return;
    }

    setLoading(true);
    setError('');
    setPreview('');
    clearPending();

    try {
      const response = await api.post('/guest-ask', {
        question: cleanQuestion,
        language,
      });

      const data = response?.data || {};
      if (!data.preview || !data.guest_token) {
        throw new Error('The server did not return a valid cosmic message.');
      }

      setPreview(data.preview);
      sessionStorage.setItem('pendingGuestQuestion', cleanQuestion);
      sessionStorage.setItem('pendingGuestPreview', data.preview);
      sessionStorage.setItem('pendingGuestToken', data.guest_token);
      sessionStorage.setItem('pendingGuestCreatedAt', data.created_at || new Date().toISOString());

      setTimeout(() => {
        document.getElementById('guest-answer')?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } catch (requestError) {
      const message = requestError?.response?.data?.detail || requestError?.message || 'The Universe could not answer right now.';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleUnlock = () => {
    navigate('/signup', {
      state: { fromGuestAsk: true, returnTo: '/dashboard' },
    });
  };

  const handleAnother = () => {
    clearPending();
    setQuestion('');
    setPreview('');
    setError('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen relative overflow-hidden text-white">
      <CosmicBackground />
      <div
        className="fixed inset-0 bg-gradient-to-b from-[#1e3a8a] via-[#0f172a] to-black pointer-events-none"
        style={{ zIndex: 1 }}
      />

      <div className="relative z-10 min-h-screen">
        <header className="px-6 sm:px-12 pt-5 pb-5">
          <div className="max-w-6xl mx-auto flex items-center justify-between">
            <Logo size="sm" />
            <div className="flex items-center gap-4">
              <LanguageSelector />
              <button onClick={() => navigate('/')} className="flex items-center gap-2 text-sm text-slate-300 hover:text-white">
                <ArrowLeft className="w-4 h-4" /> {copy.back}
              </button>
            </div>
          </div>
        </header>

        <main className="px-6 pb-20">
          <motion.section
            initial={{ opacity: 0, y: 25 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl mx-auto text-center pt-8 sm:pt-14"
          >
            <div className="flex justify-center mb-10 relative">
              <div className="absolute inset-0 w-96 h-96 left-1/2 -translate-x-1/2 -top-40 pointer-events-none">
                {[1,2,3,4,5,6].map((n) => <div key={n} className={`floating-star floating-star-${n}`} />)}
              </div>
              <div className="energy-core">
                {[1,2,3,4,5,6,7,8,9,10,11,12].map((n) => <div key={n} className={`particle particle-${n}`} />)}
                <div className="core-glow" />
              </div>
            </div>

            <p className="text-xs tracking-[0.3em] text-cyan-300/80 mb-3">{copy.eyebrow}</p>
            <h1 className="font-heading text-4xl sm:text-6xl font-light tracking-wider">{copy.title}</h1>
            <p className="max-w-2xl mx-auto mt-5 text-slate-300 font-light leading-relaxed">{copy.subtitle}</p>

            <form onSubmit={handleSubmit} className="glass-card mt-9 p-6 sm:p-9 text-left">
              <label htmlFor="guest-question" className="block text-sm text-slate-300 mb-3">{copy.label}</label>
              <textarea
                id="guest-question"
                value={question}
                onChange={(e) => { setQuestion(e.target.value); if (error) setError(''); }}
                placeholder={copy.placeholder}
                rows={6}
                maxLength={1000}
                disabled={loading}
                className="w-full resize-none rounded-2xl border border-white/15 bg-black/25 px-5 py-5 text-white placeholder:text-slate-500 outline-none focus:border-cyan-400/60 focus:ring-2 focus:ring-cyan-400/20"
              />
              <div className="mt-2 flex justify-between text-xs">
                <span className="text-rose-300">{error}</span>
                <span className="text-slate-500">{question.length}/1000</span>
              </div>
              <motion.button
                whileHover={{ scale: 1.015 }}
                whileTap={{ scale: 0.985 }}
                type="submit"
                disabled={loading || !question.trim()}
                className="cta-button mt-5 w-full rounded-full py-4 flex items-center justify-center gap-3 disabled:opacity-50"
              >
                {loading ? <Sparkles className="w-5 h-5 animate-pulse" /> : <Send className="w-5 h-5" />}
                {loading ? copy.loading : copy.submit}
              </motion.button>
            </form>
          </motion.section>

          {preview && (
            <motion.section
              id="guest-answer"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              className="max-w-3xl mx-auto mt-12"
            >
              <div className="glass-card p-7 sm:p-10 border-cyan-400/20 relative overflow-hidden">
                <p className="text-xs tracking-[0.28em] text-cyan-300/80 text-center">{copy.begun}</p>
                <h2 className="font-heading text-2xl sm:text-3xl text-center mt-3 mb-7">{copy.answered}</h2>
                <div className="rounded-2xl bg-black/20 border border-white/10 p-5 text-slate-200 leading-8 text-lg">{preview}</div>

                <div className="relative mt-1 h-40 overflow-hidden rounded-b-2xl">
                  <div className="absolute inset-0 px-5 pt-3 text-slate-400 blur-[5px] select-none" aria-hidden="true">
                    Your complete personalized message continues here with deeper meaning, guidance and clarity. The next part reveals what this energy means for your path and what you should understand now.
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-b from-transparent via-slate-950/75 to-slate-950 flex flex-col items-center justify-end pb-4 text-center px-5">
                    <LockKeyhole className="w-8 h-8 text-cyan-300 mb-2" />
                    <strong className="text-white">{copy.lockedTitle}</strong>
                    <span className="text-sm text-slate-400 mt-1 max-w-lg">{copy.lockedText}</span>
                  </div>
                </div>

                <button onClick={handleUnlock} className="cta-button w-full rounded-full py-4 mt-6">{copy.unlock}</button>
                <button onClick={handleAnother} className="w-full py-3 mt-3 text-slate-400 hover:text-white">{copy.another}</button>
                <p className="text-center text-xs text-slate-600 mt-5">{copy.disclaimer}</p>
              </div>
            </motion.section>
          )}

          <p className="text-center text-xs text-slate-600 mt-10">
            <Link to="/terms" className="hover:text-white">Terms</Link> · <Link to="/privacy" className="hover:text-white">Privacy</Link> · <Link to="/disclaimer" className="hover:text-white">Disclaimer</Link>
          </p>
        </main>
      </div>
    </div>
  );
};

export default GuestAsk;
